import { LoaderCircle, Trophy } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { partnersApi } from '../api/endpoints'
import type { FourCResultResponse } from '../types/api'

/**
 * One 4C test as mySATHI returns it (data is a list of these, one per attempt):
 * { testId, examDate, overallScore, skills: { criticalThinking, creativity, collaboration,
 * communication }, strongestCompetency }. A skill may be a bare score or an object holding one.
 */
interface FourCTest {
  testId?: unknown
  examDate?: unknown
  overallScore?: unknown
  skills?: Record<string, unknown>
  strongestCompetency?: unknown
}

/** The four Cs, in the order mySATHI's own result page shows them. */
const SKILLS: { key: string; label: string }[] = [
  { key: 'criticalThinking', label: 'Critical Thinking' },
  { key: 'collaboration', label: 'Collaboration' },
  { key: 'creativity', label: 'Creativity' },
  { key: 'communication', label: 'Communication' },
]

const VALUE_KEYS = ['score', 'value', 'marks', 'percentage', 'percent', 'result']

const humanize = (key: string) =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())

/** A score that is a number or text, or an object carrying one under a usual name. */
function scoreOf(v: unknown): string | null {
  if (typeof v === 'number') return String(v)
  if (typeof v === 'string' && v.trim() !== '') return v.trim()
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const o = v as Record<string, unknown>
    const k = Object.keys(o).find((x) => VALUE_KEYS.includes(x.toLowerCase()))
    if (k) return scoreOf(o[k])
  }
  return null
}

/** The strongest competency as a name - mySATHI may send a label, a key like "criticalThinking", or an object. */
function competencyName(v: unknown): string | null {
  if (typeof v === 'string' && v.trim() !== '') {
    const known = SKILLS.find((s) => s.key.toLowerCase() === v.trim().toLowerCase())
    return known ? known.label : humanize(v.trim())
  }
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const o = v as Record<string, unknown>
    for (const k of ['name', 'label', 'skill', 'competency', 'title']) {
      if (typeof o[k] === 'string') return competencyName(o[k])
    }
  }
  return null
}

const time = (v: unknown) => {
  const t = typeof v === 'string' || typeof v === 'number' ? new Date(v).getTime() : NaN
  return Number.isNaN(t) ? null : t
}

const formatDate = (v: unknown) => {
  const t = time(v)
  if (t === null) return typeof v === 'string' ? v : null
  return new Date(t).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** The attempts in the response, latest first (by exam date; mySATHI's own order when undated). */
function testsIn(result: unknown): FourCTest[] {
  const list = Array.isArray(result) ? result : result && typeof result === 'object' ? [result] : []
  const tests = list.filter((t): t is FourCTest => !!t && typeof t === 'object')
  return tests
    .map((t, i) => ({ t, i, at: time(t.examDate) }))
    .sort((a, b) => (a.at !== null && b.at !== null ? b.at - a.at : a.i - b.i))
    .map((x) => x.t)
}

/**
 * The 4C Assessment score from mySATHI, on its Certification card. Asked again whenever the tab comes
 * back into view - they take the test in a new tab, so returning here should show the new score.
 */
export function FourCScore({
  onScored,
}: {
  /** Told whether a score is showing, so the card can offer the report instead of the test. */
  onScored?: (scored: boolean) => void
} = {}) {
  const [res, setRes] = useState<FourCResultResponse | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    partnersApi
      .fourCResult()
      .then(setRes)
      .catch(() => setRes({ status: 'UNAVAILABLE', result: null }))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
    const onVisible = () => document.visibilityState === 'visible' && load()
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [load])

  const scored = res?.status === 'AVAILABLE' && testsIn(res.result).length > 0
  useEffect(() => {
    onScored?.(scored)
  }, [scored, onScored])

  const box = 'mt-4 w-full rounded-xl border border-ink-200/70 bg-ink-50 p-3 text-sm'
  const heading = <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-400">Your score</div>

  if (loading && !res) {
    return (
      <div className={`${box} flex items-center gap-2 text-ink-500`}>
        <LoaderCircle size={14} className="animate-spin" /> Checking your score...
      </div>
    )
  }

  const tests = res?.status === 'AVAILABLE' ? testsIn(res.result) : []
  const latest = tests[0]
  if (!latest) {
    const text =
      res?.status === 'AVAILABLE' || res?.status === 'NOT_TAKEN'
        ? 'No score yet - take the assessment and your score will show here.'
        : res?.status === 'NO_EMAIL'
          ? 'Add an email to your profile to see your score here.'
          : 'Your score could not be loaded right now.'
    return (
      <div className={box}>
        {heading}
        <p className="mt-1 text-ink-500">{text}</p>
      </div>
    )
  }

  const overall = scoreOf(latest.overallScore)
  const skills = latest.skills ?? {}
  // The four Cs first, in order; anything else mySATHI adds to skills after them, by its own name.
  const rows = [
    ...SKILLS.map((s) => ({ label: s.label, value: scoreOf(skills[s.key]) })),
    ...Object.keys(skills)
      .filter((k) => !SKILLS.some((s) => s.key === k))
      .map((k) => ({ label: humanize(k), value: scoreOf(skills[k]) })),
  ].filter((r): r is { label: string; value: string } => r.value !== null)
  const strongest = competencyName(latest.strongestCompetency)
  const date = formatDate(latest.examDate)

  return (
    <div className={box}>
      <div className="flex items-start justify-between gap-2">
        {heading}
        {date && <span className="text-xs text-ink-500">Taken {date}</span>}
      </div>

      {overall && (
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-3xl font-extrabold tabular-nums text-ink-900">{overall}</span>
          <span className="text-ink-500">Overall score</span>
        </div>
      )}

      {rows.length > 0 && (
        <dl className="mt-2 grid grid-cols-2 gap-2">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between gap-2 rounded-lg bg-white px-2.5 py-2">
              <dt className="truncate text-xs font-medium text-ink-600" title={r.label}>
                {r.label}
              </dt>
              <dd className="text-base font-bold tabular-nums text-ink-900">{r.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {strongest && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-ink-600">
          <Trophy size={13} className="shrink-0 text-sun-600" />
          Strongest competency: <span className="font-semibold text-ink-900">{strongest}</span>
        </p>
      )}

      {tests.length > 1 && (
        <p className="mt-1 text-xs text-ink-400">
          Latest of {tests.length} attempts.
        </p>
      )}
    </div>
  )
}
